/**
 * Melting surcharge: keep a surcharge variant at qty 0 or 1 based on cart contents.
 * Config: window.cartMeltingSurcharge (from theme settings).
 */
(function () {
  if (typeof subscribe !== 'function' || typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
    return;
  }
  if (typeof window.routes === 'undefined' || !window.routes.cart_url || !window.routes.cart_add_url) {
    return;
  }

  let syncing = false;
  let pendingResync = false;

  function parseQualifyingTypes(raw) {
    if (raw == null || String(raw).trim() === '') return [];
    return String(raw)
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
  }

  function getConfig() {
    const c = window.cartMeltingSurcharge;
    if (!c || !c.enabled) return null;
    const vid = c.variantId;
    const n = typeof vid === 'number' ? vid : parseInt(String(vid), 10);
    if (!Number.isFinite(n) || n < 1) return null;
    return {
      variantId: n,
      qualifyingTypes: parseQualifyingTypes(c.qualifyingProductTypesRaw),
    };
  }

  function bundleIdFromItem(item) {
    const p = item.properties;
    if (p == null) return null;
    if (typeof p === 'object' && !Array.isArray(p)) {
      const v = p._rc_bundle;
      return v != null && String(v) !== '' ? String(v) : null;
    }
    if (Array.isArray(p)) {
      for (let i = 0; i < p.length; i++) {
        const row = p[i];
        if (!row) continue;
        const k = row.name != null ? row.name : row.key;
        if (k === '_rc_bundle' && row.value != null && String(row.value) !== '') {
          return String(row.value);
        }
      }
    }
    return null;
  }

  function lineQualifiesByType(item, qualifyingTypes) {
    if (!qualifyingTypes.length) return false;
    const pt = String(item.product_type || '')
      .trim()
      .toLowerCase();
    return qualifyingTypes.indexOf(pt) !== -1;
  }

  /**
   * True if there is at least one one-time line (no selling plan) that matches
   * qualifying product types, with quantity >= 1. Recharge bundle: one logical bundle
   * per _rc_bundle id (same as cart-drawer subscription grouping).
   */
  function hasQualifyingOnetimePurchase(cart, config) {
    if (!cart || !cart.items || cart.items.length === 0) return false;
    const seenBundles = new Set();
    const vid = config.variantId;

    for (let i = 0; i < cart.items.length; i++) {
      const item = cart.items[i];
      if (Number(item.variant_id) === vid) continue;

      if (item.selling_plan_allocation != null) continue;

      if (!lineQualifiesByType(item, config.qualifyingTypes)) continue;

      const qty = Number(item.quantity);
      if (!Number.isFinite(qty) || qty < 1) continue;

      const bid = bundleIdFromItem(item);
      if (bid) {
        if (seenBundles.has(bid)) continue;
        seenBundles.add(bid);
      }

      return true;
    }
    return false;
  }

  function surchargeLines(cart, variantId) {
    const out = [];
    if (!cart || !cart.items) return out;
    cart.items.forEach((item) => {
      if (Number(item.variant_id) === variantId) {
        out.push(item);
      }
    });
    return out;
  }

  function totalSurchargeQty(lines) {
    let t = 0;
    lines.forEach((item) => {
      const q = Number(item.quantity);
      if (Number.isFinite(q)) t += q;
    });
    return t;
  }

  function cartUpdateJsUrl() {
    const u = window.routes.cart_update_url;
    return u.indexOf('.js') !== -1 ? u : `${u}.js`;
  }

  function cartAddJsUrl() {
    const u = window.routes.cart_add_url;
    return u.indexOf('.js') !== -1 ? u : `${u}.js`;
  }

  /**
   * @returns {Promise<object>} cart JSON
   */
  async function applyMeltingChange(config, desiredQty, cart) {
    const variantId = config.variantId;
    const lines = surchargeLines(cart, variantId);
    const currentTotal = totalSurchargeQty(lines);

    if (desiredQty === currentTotal && lines.length <= 1) {
      return cart;
    }

    const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };

    if (desiredQty === 0) {
      if (lines.length === 0) return cart;
      const updates = {};
      lines.forEach((item) => {
        updates[item.key] = 0;
      });
      const res = await fetch(cartUpdateJsUrl(), {
        method: 'POST',
        headers,
        body: JSON.stringify({ updates }),
      });
      return res.json();
    }

    if (lines.length === 0) {
      const res = await fetch(cartAddJsUrl(), {
        method: 'POST',
        headers,
        body: JSON.stringify({ items: [{ id: variantId, quantity: desiredQty }] }),
      });
      return res.json();
    }

    const updates = {};
    if (lines.length === 1) {
      updates[lines[0].key] = desiredQty;
    } else {
      updates[lines[0].key] = desiredQty;
      for (let i = 1; i < lines.length; i++) {
        updates[lines[i].key] = 0;
      }
    }

    const res = await fetch(cartUpdateJsUrl(), {
      method: 'POST',
      headers,
      body: JSON.stringify({ updates }),
    });
    return res.json();
  }

  async function fetchCartJson() {
    const res = await fetch(`${window.routes.cart_url}.js`);
    return res.json();
  }

  async function sync(eventCartData) {
    if (syncing) {
      pendingResync = true;
      return;
    }

    const config = getConfig();
    if (!config) return;

    syncing = true;
    try {
      let cart =
        eventCartData && Array.isArray(eventCartData.items) ? eventCartData : await fetchCartJson();

      const desired = hasQualifyingOnetimePurchase(cart, config) ? 1 : 0;
      const lines = surchargeLines(cart, config.variantId);
      const currentTotal = totalSurchargeQty(lines);

      if (desired === currentTotal && lines.length <= 1) {
        return;
      }

      const updated = await applyMeltingChange(config, desired, cart);
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'melting-surcharge', cartData: updated });
    } catch (e) {
      console.error('[cart-melting-surcharge]', e);
    } finally {
      syncing = false;
      if (pendingResync) {
        pendingResync = false;
        sync(null);
      }
    }
  }

  let debounceTimer = null;
  function scheduleSync(eventCartData) {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      sync(eventCartData);
    }, 80);
  }

  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (!event || event.source === 'melting-surcharge') return;
    scheduleSync(event.cartData);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scheduleSync(null));
  } else {
    scheduleSync(null);
  }
})();
